import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'

import { fetchPublicApiDataIfNeeded, callPublicApi } from '../../../actions/api'
import { apiResourceEndpoint } from '../../../constants/routes'
import { upsertForm } from '../../../reducers/form'
import { formStates } from '../../../constants/helper-states'

import InputElement from '../../utils/input'
import * as MASKS from '../../utils/input-masks'
import SHSForm from '../../utils/shs-form'
import { sortOptions } from '../../utils/option-sort'
import { toWholeNumber } from '../../utils/formatters'
import { displayDateKey } from '../../utils/date-keys'
import FormSection from './form-section'
import InquiryBackLink from './back-link'
import { OptionButtons, MultiOptionButtons } from './option-buttons'
import DateRangeSchedule from './date-range-schedule'
import { NEEDS_VENUE_OPTIONS, BREAKFAST_STYLE_OPTIONS, LUNCH_STYLE_OPTIONS, DINNER_STYLE_OPTIONS, KITCHEN_MANAGEMENT_OPTIONS, ALLERGY_COUNT_OPTIONS, PREFERRED_CONTACT_OPTIONS, YES_NO_OPTIONS } from '../../../constants/inquiry-options'

const SECTIONS = ['experience', 'menu', 'dietary', 'about']
const ANIMATION_DURATION = 320

const INITIAL_FIELDS = {
    formType: 'multi_day',
    eventDate: '',
    endDate: '',
    mealSchedule: [],
    headcount: '',
    apiOccasionId: '',
    city: '',
    state: '',
    street: '',
    postal: '',
    needsVenue: '',
    breakfastServiceStyle: '',
    lunchServiceStyle: '',
    dinnerServiceStyle: '',
    kitchenManagement: '',
    allergies: [],
    dietaryPreferences: [],
    allergyCount: '',
    dislikes: '',
    hostingReason: '',
    message: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    preferredContact: '',
    needsRentals: '',
    isFirstTime: '',
    hasGiftCard: '',
    referralSource: '',
    mailingList: false
}

const mapStateToProps = (state) => ({
    apiOccasions: state.apiOccasions,
    apiAllergens: state.apiAllergens,
    apiDietaryRestrictions: state.apiDietaryRestrictions
})

const MultiDayInquiry = (props) => {
    const { dispatch, apiOccasions, apiAllergens, apiDietaryRestrictions } = props

    const [activeSection, setActiveSection] = useState(0)
    const [completedSections, setCompletedSections] = useState([])
    const [completingSection, setCompletingSection] = useState(null)
    const [submitted, setSubmitted] = useState(false)
    const [errors, setErrors] = useState({})
    const [fields, setFields] = useState(INITIAL_FIELDS)

    useEffect(() => {
        dispatch(fetchPublicApiDataIfNeeded(apiResourceEndpoint('public/api-occasions', 'LIST')))
        dispatch(fetchPublicApiDataIfNeeded(apiResourceEndpoint('public/api-allergens', 'LIST')))
        dispatch(fetchPublicApiDataIfNeeded(apiResourceEndpoint('public/api-dietary-restrictions', 'LIST')))
    }, [dispatch])

    const handleChange = (field, value) => {
        setFields(prev => ({ ...prev, [field]: value }))
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }))
        }
    }

    const handleDatesChange = (startKey, endKey) => {
        setFields(prev => ({ ...prev, eventDate: startKey, endDate: endKey }))
    }

    const handleScheduleChange = (newSchedule) => {
        setFields(prev => ({ ...prev, mealSchedule: newSchedule }))
    }

    const sectionStatus = (idx) => {
        if (idx === completingSection) {
            return 'completing'
        }
        if (completedSections.includes(idx)) {
            return 'complete'
        }
        if (activeSection === idx) {
            return 'active'
        }
        return 'locked'
    }

    const editSection = (idx) => {
        setActiveSection(idx)
        setCompletedSections(prev => prev.filter(s => s !== idx))
    }

    const advanceSection = (idx) => {
        setErrors({})
        setCompletingSection(idx)
        setTimeout(() => {
            setCompletingSection(null)
            const next = idx + 1
            setCompletedSections(prev => {
                const updated = [...prev, idx]
                return next < SECTIONS.length ? updated.filter(s => s !== next) : updated
            })
            if (next < SECTIONS.length) {
                setActiveSection(next)
                setTimeout(() => {
                    const el = document.getElementById(`section-${next}`)
                    if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                }, 80)
            }
        }, ANIMATION_DURATION)
    }

    const handleSection0 = (formData, sectionFormId) => {
        dispatch(upsertForm({ id: sectionFormId, status: formStates.EMPTY }))
        const customErrors = {}
        if (!fields.eventDate) {
            customErrors.eventDate = 'Required'
        }
        if (!fields.endDate) {
            customErrors.endDate = 'Required'
        }
        if (!fields.mealSchedule || fields.mealSchedule.length === 0) {
            customErrors.mealSchedule = 'Please select meals for at least one day'
        }
        if (!fields.needsVenue) {
            customErrors.needsVenue = 'Required'
        }
        if (Object.keys(customErrors).length > 0) {
            setErrors(customErrors)
            return
        }
        advanceSection(0)
    }

    const handleSection1 = (formData, sectionFormId) => {
        dispatch(upsertForm({ id: sectionFormId, status: formStates.EMPTY }))
        const customErrors = {}
        if (!fields.kitchenManagement) {
            customErrors.kitchenManagement = 'Required'
        }
        if (Object.keys(customErrors).length > 0) {
            setErrors(customErrors)
            return
        }
        advanceSection(1)
    }

    const handleSection2 = (formData, sectionFormId) => {
        dispatch(upsertForm({ id: sectionFormId, status: formStates.EMPTY }))
        const customErrors = {}
        if (!fields.allergyCount) {
            customErrors.allergyCount = 'Required'
        }
        if (Object.keys(customErrors).length > 0) {
            setErrors(customErrors)
            return
        }
        advanceSection(2)
    }

    // The form asks for one style per meal type; store it against each day so an
    // individual date can be adjusted later from the secondary response form.
    const buildPayload = () => {
        const styleByMeal = {
            breakfast: fields.breakfastServiceStyle,
            lunch: fields.lunchServiceStyle,
            dinner: fields.dinnerServiceStyle
        }
        const {
            breakfastServiceStyle, lunchServiceStyle, dinnerServiceStyle, ...rest
        } = fields
        return {
            ...rest,
            headcount: toWholeNumber(fields.headcount),
            mealSchedule: (fields.mealSchedule || []).map(day => {
                const styles = {}
                const meals = day.meals || []
                meals.forEach(meal => {
                    if (styleByMeal[meal]) {
                        styles[meal] = styleByMeal[meal]
                    }
                })
                return { ...day, styles }
            })
        }
    }

    const handleSection3 = (formData, sectionFormId) => {
        dispatch(callPublicApi(apiResourceEndpoint('public/event-forms', 'CREATE'), buildPayload(), sectionFormId))
    }

    const handleSuccess = () => {
        setSubmitted(true)
        window.scrollTo(0, 0)
    }

    const occasionOptions = sortOptions(apiOccasions).map(o => ({ value: o.id, label: o.name }))
    const allergenOptions = sortOptions(apiAllergens).map(a => ({ value: a.id, label: a.name }))
    const dietaryOptions = sortOptions(apiDietaryRestrictions).map(d => ({ value: d.id, label: d.name }))

    const fallbackAllergenOptions = [
        { value: 'shellfish', label: 'Shellfish' },
        { value: 'fish', label: 'Fish' },
        { value: 'dairy', label: 'Dairy' },
        { value: 'egg', label: 'Egg' },
        { value: 'peanut', label: 'Peanut' },
        { value: 'tree_nut', label: 'Tree Nut' },
        { value: 'soy', label: 'Soy' },
        { value: 'wheat', label: 'Wheat' },
        { value: 'gluten', label: 'Gluten' },
        { value: 'celiac', label: 'Celiac' }
    ]

    const fallbackDietaryOptions = [
        { value: 'pescatarian', label: 'Pescatarian' },
        { value: 'vegetarian', label: 'Vegetarian' },
        { value: 'vegan', label: 'Vegan' },
        { value: 'keto', label: 'Keto' }
    ]

    const shownAllergenOptions = allergenOptions.length > 0 ? allergenOptions : fallbackAllergenOptions
    const shownDietaryOptions = dietaryOptions.length > 0 ? dietaryOptions : fallbackDietaryOptions

    const totalDays = fields.mealSchedule ? fields.mealSchedule.length : 0

    // Which meal types appear anywhere in the schedule — drives which style
    // questions are asked in The Format.
    const scheduledMeals = (fields.mealSchedule || []).reduce((acc, day) => {
        const meals = day.meals || []
        meals.forEach(meal => {
            if (!acc.includes(meal)) {
                acc.push(meal)
            }
        })
        return acc
    }, [])

    const styleLabel = (options, value) => {
        const found = options.find(o => o.value === value)
        return found ? found.label : null
    }

    const section0Summary = [
        fields.eventDate && fields.endDate ? `${displayDateKey(fields.eventDate)} to ${displayDateKey(fields.endDate)}` : null,
        totalDays > 0 ? `${totalDays} day${totalDays !== 1 ? 's' : ''}` : null,
        toWholeNumber(fields.headcount) ? `${toWholeNumber(fields.headcount).toLocaleString()} guests` : null,
        [fields.city, fields.state].filter(Boolean).join(', ')
    ].filter(Boolean).join(' · ')

    const breakfastLabel = styleLabel(BREAKFAST_STYLE_OPTIONS, fields.breakfastServiceStyle)
    const lunchLabel = styleLabel(LUNCH_STYLE_OPTIONS, fields.lunchServiceStyle)
    const dinnerLabel = styleLabel(DINNER_STYLE_OPTIONS, fields.dinnerServiceStyle)

    const section1Summary = [
        breakfastLabel ? `Breakfast: ${breakfastLabel}` : null,
        lunchLabel ? `Lunch: ${lunchLabel}` : null,
        dinnerLabel ? `Dinner: ${dinnerLabel}` : null,
        styleLabel(KITCHEN_MANAGEMENT_OPTIONS, fields.kitchenManagement)
    ].filter(Boolean).join(' · ')

    const section2Summary = [
        fields.allergies && fields.allergies.length > 0
            ? shownAllergenOptions.filter(a => fields.allergies.includes(a.value)).map(a => a.label).join(', ')
            : 'No allergies noted',
        fields.dislikes ? `Dislikes: ${fields.dislikes}` : null
    ].filter(Boolean).join(' · ')

    const section3Summary = [
        [fields.firstName, fields.lastName].filter(Boolean).join(' '),
        fields.email
    ].filter(Boolean).join(' · ')

    if (submitted) {
        return (
            <>
                <div className="full-page-bg-img" style={{ backgroundImage: 'url("/img/bg/place-setting.jpg")' }} />
                <div className="form-holder">
                    <div className="event-form">
                        <div className="form-header">
                            <img src="/img/icons/tivity-logo.png" alt="Tivity" width="120px" />
                            <h1>{`You're all set!`}</h1>
                            <p>Thank you for your inquiry. Someone from the Tivity team will reach out within 24 hours with chef, menu, and pricing options.</p>
                        </div>
                    </div>
                </div>
            </>
        )
    }

    return (
        <>
            <div className="full-page-bg-img" style={{ backgroundImage: 'url("/img/bg/place-setting.jpg")' }} />
            <div className="form-holder inquiry-form-holder">
                <div className="event-form inquiry-form">
                    <InquiryBackLink />
                    <div className="form-header">
                        <img src="/img/icons/tivity-logo.png" alt="Tivity" width="120px" />
                        <h1>Multi-Day Experience</h1>
                        <p>Tell us about your experience. We will reach back out within 24 hours with options.</p>
                    </div>

                    <div id="section-0">
                        <FormSection
                            number={1}
                            title="Your Experience"
                            status={sectionStatus(0)}
                            summary={section0Summary}
                            onEdit={() => editSection(0)}>
                            <SHSForm
                                submitFX={handleSection0}
                                successFX={() => {}}
                                buttonLabel="Continue to Format &rarr;"
                                formClass="inquiry-section__continue flex-100">
                                <InputElement
                                    type="text"
                                    id="firstName"
                                    label="First Name"
                                    default={fields.firstName}
                                    onChange={handleChange}
                                    placeholder="First Name"
                                    extraClass="flex-50"
                                    required />
                                <InputElement
                                    type="text"
                                    id="lastName"
                                    label="Last Name"
                                    default={fields.lastName}
                                    onChange={handleChange}
                                    placeholder="Last Name"
                                    extraClass="flex-50"
                                    required />
                                <InputElement
                                    type="tel"
                                    id="phone"
                                    label="Cell Phone"
                                    default={fields.phone}
                                    onChange={handleChange}
                                    isFormatted={true}
                                    formatMask={MASKS.phoneMask}
                                    placeholder="(555) 000-0000"
                                    extraClass="flex-50"
                                    required />
                                <InputElement
                                    type="email"
                                    id="email"
                                    label="Email"
                                    default={fields.email}
                                    onChange={handleChange}
                                    placeholder="your@email.com"
                                    extraClass="flex-50"
                                    required />
                                <DateRangeSchedule
                                    startKey={fields.eventDate}
                                    endKey={fields.endDate}
                                    schedule={fields.mealSchedule}
                                    onScheduleChange={handleScheduleChange}
                                    onDatesChange={handleDatesChange}
                                    hasError={!!(errors.eventDate || errors.endDate)} />
                                {errors.mealSchedule && (
                                    <div className="inquiry-field-error flex-100">{errors.mealSchedule}</div>
                                )}
                                <InputElement
                                    type="text"
                                    id="headcount"
                                    label="Number of Guests"
                                    default={fields.headcount}
                                    onChange={handleChange}
                                    placeholder="e.g. 8"
                                    isFormatted={true}
                                    formatMask={MASKS.countMask}
                                    extraClass="flex-50"
                                    required />
                                <InputElement
                                    type="valueSelect"
                                    id="apiOccasionId"
                                    label="Occasion"
                                    default={fields.apiOccasionId}
                                    onChange={handleChange}
                                    initialValueLabel="What is the occasion?"
                                    options={occasionOptions}
                                    extraClass="flex-50" />
                                <InputElement
                                    type="textarea"
                                    id="hostingReason"
                                    label="What brings everyone together?"
                                    default={fields.hostingReason}
                                    onChange={handleChange}
                                    placeholder="e.g. Family reunion, corporate retreat, anniversary trip..."
                                    extraClass="flex-100" />
                                <InputElement
                                    type="text"
                                    id="city"
                                    label="City"
                                    default={fields.city}
                                    onChange={handleChange}
                                    placeholder="City"
                                    extraClass="flex-50"
                                    required />
                                <InputElement
                                    type="text"
                                    id="state"
                                    label="State"
                                    default={fields.state}
                                    onChange={handleChange}
                                    placeholder="State"
                                    extraClass="flex-50"
                                    required />
                                <InputElement
                                    type="text"
                                    id="postal"
                                    label="Zip Code"
                                    default={fields.postal}
                                    onChange={handleChange}
                                    placeholder="Zip code"
                                    extraClass="flex-50"
                                    required />
                                <InputElement
                                    type="mapboxAddress"
                                    id="street"
                                    label="Street Address"
                                    default={fields.street}
                                    onChange={handleChange}
                                    placeholder="Street address"
                                    autoComplete="street-address"
                                    extraClass="flex-50" />
                                <OptionButtons
                                    id="needsVenue"
                                    label="Do you need a venue?"
                                    error={errors.needsVenue}
                                    options={NEEDS_VENUE_OPTIONS}
                                    value={fields.needsVenue}
                                    onChange={handleChange}
                                    required />
                            </SHSForm>
                        </FormSection>
                    </div>

                    <div id="section-1">
                        <FormSection
                            number={2}
                            title="The Format"
                            status={sectionStatus(1)}
                            summary={section1Summary}
                            onEdit={() => editSection(1)}>
                            <SHSForm
                                submitFX={handleSection1}
                                successFX={() => {}}
                                buttonLabel="Continue to Dietary &rarr;"
                                formClass="inquiry-section__continue flex-100">
                                {scheduledMeals.includes('breakfast') && (
                                    <OptionButtons
                                        id="breakfastServiceStyle"
                                        label="Breakfast Service Style"
                                        options={BREAKFAST_STYLE_OPTIONS}
                                        value={fields.breakfastServiceStyle}
                                        onChange={handleChange} />
                                )}
                                {scheduledMeals.includes('lunch') && (
                                    <OptionButtons
                                        id="lunchServiceStyle"
                                        label="Lunch Service Style"
                                        options={LUNCH_STYLE_OPTIONS}
                                        value={fields.lunchServiceStyle}
                                        onChange={handleChange} />
                                )}
                                {scheduledMeals.includes('dinner') && (
                                    <OptionButtons
                                        id="dinnerServiceStyle"
                                        label="Dinner Service Style"
                                        options={DINNER_STYLE_OPTIONS}
                                        value={fields.dinnerServiceStyle}
                                        onChange={handleChange} />
                                )}
                                <OptionButtons
                                    id="kitchenManagement"
                                    label="Do you want the chef to manage the kitchen and cleanup throughout the day, or just prepare the meals?"
                                    error={errors.kitchenManagement}
                                    options={KITCHEN_MANAGEMENT_OPTIONS}
                                    value={fields.kitchenManagement}
                                    onChange={handleChange}
                                    required />
                                <InputElement
                                    type="textarea"
                                    id="message"
                                    label="Any additional requests?"
                                    default={fields.message}
                                    onChange={handleChange}
                                    placeholder="Anything else we should know..."
                                    extraClass="flex-100" />
                            </SHSForm>
                        </FormSection>
                    </div>

                    <div id="section-2">
                        <FormSection
                            number={3}
                            title="Dietary & Details"
                            status={sectionStatus(2)}
                            summary={section2Summary}
                            onEdit={() => editSection(2)}>
                            <SHSForm
                                submitFX={handleSection2}
                                successFX={() => {}}
                                buttonLabel="Continue to Event Items &rarr;"
                                formClass="inquiry-section__continue flex-100">
                                <MultiOptionButtons
                                    id="allergies"
                                    label="Allergies (select all that apply)"
                                    options={shownAllergenOptions}
                                    value={fields.allergies}
                                    onChange={handleChange} />
                                <MultiOptionButtons
                                    id="dietaryPreferences"
                                    label="Dietary Preferences"
                                    options={shownDietaryOptions}
                                    value={fields.dietaryPreferences}
                                    onChange={handleChange} />
                                <OptionButtons
                                    id="allergyCount"
                                    label="How many guests have an allergy or dietary restriction?"
                                    error={errors.allergyCount}
                                    options={ALLERGY_COUNT_OPTIONS}
                                    value={fields.allergyCount}
                                    onChange={handleChange}
                                    required />
                                <InputElement
                                    type="textarea"
                                    id="dislikes"
                                    label="List anything you or your guests do NOT like to eat"
                                    default={fields.dislikes}
                                    onChange={handleChange}
                                    placeholder="e.g. No mushrooms, hate cilantro..."
                                    extraClass="flex-100" />
                            </SHSForm>
                        </FormSection>
                    </div>

                    <div id="section-3">
                        <FormSection
                            number={4}
                            title="Additional Event Items"
                            status={sectionStatus(3)}
                            summary={section3Summary}
                            onEdit={() => editSection(3)}>
                            <SHSForm
                                submitFX={handleSection3}
                                successFX={handleSuccess}
                                buttonLabel="Submit Inquiry"
                                formClass="inquiry-section__continue flex-100">
                                <OptionButtons
                                    id="preferredContact"
                                    label="Preferred contact method"
                                    options={PREFERRED_CONTACT_OPTIONS}
                                    value={fields.preferredContact}
                                    onChange={handleChange} />
                                <OptionButtons
                                    id="needsRentals"
                                    label="Do you need glassware, plateware, or flatware?"
                                    options={YES_NO_OPTIONS}
                                    value={fields.needsRentals}
                                    onChange={handleChange} />
                                <OptionButtons
                                    id="isFirstTime"
                                    label="Is this your first time with Tivity?"
                                    options={YES_NO_OPTIONS}
                                    value={fields.isFirstTime}
                                    onChange={handleChange} />
                                <OptionButtons
                                    id="hasGiftCard"
                                    label="Do you have a gift card to redeem?"
                                    options={YES_NO_OPTIONS}
                                    value={fields.hasGiftCard}
                                    onChange={handleChange} />
                                <InputElement
                                    type="text"
                                    id="referralSource"
                                    label="How did you hear about Tivity?"
                                    default={fields.referralSource}
                                    onChange={handleChange}
                                    placeholder="e.g. Instagram, a friend, Google..."
                                    extraClass="flex-100" />
                            </SHSForm>
                        </FormSection>
                    </div>
                </div>
            </div>
        </>
    )
}

export default connect(mapStateToProps)(MultiDayInquiry)
